import React, { forwardRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx } from '../cx'
import s from './styles.module.scss'

export type ButtonVariant = 'primary' | 'neutral' | 'danger' | 'ghost' | 'link'
export type ButtonSize = 'md' | 'sm'

interface BaseProps {
    /** Visual hierarchy. One `primary` per view; `neutral` for everything else. */
    variant?: ButtonVariant
    size?: ButtonSize
    /** Leading icon (react-icons element). */
    icon?: ReactNode
    /** Trailing icon. */
    iconRight?: ReactNode
    /** Shows a spinner and blocks interaction while keeping the width. */
    loading?: boolean
    /** Full width. */
    block?: boolean
    className?: string
    children?: ReactNode
}

type NativeButtonProps = BaseProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { as?: 'button'; to?: never; href?: never }
type AnchorProps = BaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & { as: 'a'; href: string; to?: never }
type RouterLinkProps = BaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & { as: 'link'; to: string; href?: never }

export type ButtonProps = NativeButtonProps | AnchorProps | RouterLinkProps

/**
 * The only button in the dashboard. Renders a `<button>`, `<a>` or router
 * `<Link>` depending on `as`, with the same look in every case.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
    const { variant = 'neutral', size = 'md', icon, iconRight, loading = false, block = false, className, children, ...rest } =
        props as ButtonProps & Record<string, any>

    const classes = cx(s[variant], size === 'sm' && s.sm, block && s.block, loading && s.loading, className)
    const content = (
        <>
            {icon}
            {children}
            {iconRight}
            {loading && <span className={s.spinner} aria-hidden="true" />}
        </>
    )

    if (rest.as === 'link') {
        const { as: _as, to, ...anchorRest } = rest
        return (
            <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} className={classes} {...anchorRest}>
                {content}
            </Link>
        )
    }
    if (rest.as === 'a') {
        const { as: _as, ...anchorRest } = rest
        return (
            <a ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...anchorRest}>
                {content}
            </a>
        )
    }
    const { as: _as, type = 'button', disabled, ...buttonRest } = rest
    return (
        <button
            ref={ref as React.Ref<HTMLButtonElement>}
            type={type}
            className={classes}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...buttonRest}
        >
            {content}
        </button>
    )
})
Button.displayName = 'Button'

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** Required for accessibility since there is no visible text. */
    label: string
    icon: ReactNode
    variant?: 'neutral' | 'ghost' | 'danger'
    size?: ButtonSize
}

/** Square icon-only button. Always give it a `label`. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ label, icon, variant = 'neutral', size = 'md', className, type = 'button', ...rest }, ref) => (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            title={label}
            className={cx(s.iconButton, variant !== 'neutral' && s[variant], size === 'sm' && s.sm, className)}
            {...rest}
        >
            {icon}
        </button>
    )
)
IconButton.displayName = 'IconButton'

export interface ButtonGroupProps {
    children: ReactNode
    /** Joins the buttons into one pill with shared borders. */
    attached?: boolean
    className?: string
}

export const ButtonGroup = ({ children, attached = false, className }: ButtonGroupProps) => (
    <div className={cx(s.group, attached && s.attached, className)} role="group">
        {children}
    </div>
)

export interface SegmentedOption<T extends string = string> {
    value: T
    label: ReactNode
    count?: number | string
    disabled?: boolean
}

export interface SegmentedControlProps<T extends string = string> {
    options: SegmentedOption<T>[]
    value: T
    onChange: (value: T) => void
    className?: string
    'aria-label'?: string
}

/** Pill-style single-select: filter scopes, case switchers, view toggles. */
export function SegmentedControl<T extends string = string>({
    options,
    value,
    onChange,
    className,
    ...rest
}: SegmentedControlProps<T>) {
    return (
        <div className={cx(s.segmented, className)} role="tablist" aria-label={rest['aria-label']}>
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    role="tab"
                    aria-selected={o.value === value}
                    disabled={o.disabled}
                    className={cx(s.segment, o.value === value && s.segmentActive)}
                    onClick={() => onChange(o.value)}
                >
                    {o.label}
                    {o.count != null && <span className={s.segmentCount}>{o.count}</span>}
                </button>
            ))}
        </div>
    )
}
